<template>
    <connection-settings-popup
        :is-new="isNew"
        :config="dedupFields"
        :validations="$options.validations.dedupFields"
        :comment-id="commentId"
        :resources="{
            deletePromptTitle: resource('JourneyBuilder_DedupTarget_Delete_Title'),
            deletePromptBody: resource('JourneyBuilder_DedupTarget_Delete_Body')
        }"

        @save="save()"
        @delete="deleteCheckpoint()"
        @display-errors="displayErrors()"
        @hidden="$emit('hidden')"
    >
        <template #title>
            <h3>
                {{resource('JourneyBuilder_DeduplicationTargetSettings_Title')}}
            </h3>
            <help-popover
                :title="resource('JourneyBuilder_DeduplicationTargetSettings_Title')"
                :content="resource('JourneyBuilder_DeduplicationTargetSettings_Description')"
            />
        </template>

        <template #body>
            <h4>{{resource("JourneyBuilder_JourneyBrick_SettingsHeader")}}</h4>

            <button @click="addField" class="btn btn-default mb-2" :disabled="!canWrite">
                <i class="fas fa-plus"></i> {{resource('SettingsDivisionMiscellaneous_Index_AddFieldButton_Text')}}
            </button>
            
            <div class="row mt-3" v-for="(field, index) in dedupFields">
                <label class="col-2 field-label" :for="`field_${index}`">{{resource('JourneyBuilder_DeduplicationSettings_FieldName')}}</label>

                <v-select
                    class="col-7 form-control"
                    :input-id="`field_${index}`"
                    v-model="dedupFields[index]"
                    :clearable="false"
                    :options="availableFields"
                    
                    :disabled="!canWrite"
                />

                <div v-if="canWrite" 
                    @click="removeField(index)" 
                    class="col-3 btn btn-link"
                >
                    <i class="far fa-trash-alt"></i> {{resource('SettingsDivisionMiscellaneous_Index_DeleteFieldButton_Text')}}
                </div>

                <div class="col-12" v-if="$v.$dirty && $v.dedupFields.$each[index].$invalid">
                    <required-error-message />
                </div>
            </div>

            <error-message
                v-if="!$v.dedupFields.required"
                :message="resource('JourneyComposer_DedupField_ErrorMessage')"

                class="d-block"
            />
            
            <hr />
            <div class="row">
                <div class="col-2">
                    <label class="control-label">{{resource('JourneyBuilder_DeduplicationSettings_Term_Label')}}</label>
                </div>
                <div class="col-10">
                    <time-period v-model="term" :disabled="!canWrite" />
                </div>
            </div>
        </template>
    </connection-settings-popup>
</template>

<script>
import api from "@modules/JourneyBuilder/api";
import VSelect from "vue-select";
import Modal from '@core/Components/Util/Modal.vue';
import resources from '@core/Util/Mixins/resources';
import Prompt from "@core/Components/Util/Prompt";
import HelpPopover from '@modules/JourneyBuilder/Components/Elements/HelpPopover.vue';
import TabbedMenu from "@core/Components/Navigation/TabbedMenu"
import { NewComment } from '@modules/JourneyBuilder/Components/Composer/CanvasArea/Bricks/Comments';
import RequiredErrorMessage from "@core/Components/Errors/RequiredErrorMessage";
import ErrorMessage from "@core/Components/Errors/ErrorMessage";
import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
import {validationMixin} from 'vuelidate';
import {required} from 'vuelidate/lib/validators';
import { deduplicationTargetCheckpoint } from '@modules/JourneyBuilder/Bricks/checkpoint-definitions';
import ConnectionSettingsPopup from '../ConnectionSettingsPopup';
import TimePeriod from "@core/Components/Inputs/JiraStyleTimePeriod";

export default {
    components: {
        Modal,
        Prompt,
        VSelect,
        HelpPopover,
        TabbedMenu,
        NewComment,
        RequiredErrorMessage,
        ErrorMessage,
        TimePeriod,
        ConnectionSettingsPopup
    },
    
    mixins: [ resources, validationMixin, permissionsMixin ],

    props: {
        config: Object,
        connection: Object,
        isNew: Boolean
    },

    data: () => ({
        availableFields: [],
        dedupFields: [],
        term: { seconds: 0, unit: "" }
    }),

    computed: { 
        commentId() {
            return "connection:" + this.connection.id + ":" + deduplicationTargetCheckpoint.name;
        },
    },

    validations: {
        dedupFields: {
            required,
            $each: {
                required
            }
        }
    },

    created() {
        const vm = this; 
        vm._getTouchpointDeduplicationFieldsPromise = api.getTouchpointDeduplicationFields();
        vm._getTouchpointDeduplicationFieldsPromise.then(
            opts => vm.availableFields = opts,
            err => errorGrowl(err)
        ); 
        
        if(this.config.dedupTarget && this.config.dedupTarget.term){
            this.term = this.config.dedupTarget.term;
        }
    },

    async mounted() {
        if (this.config.dedupTarget) {
            if (!this.config.dedupTarget.fields || this.config.dedupTarget.fields.length == 0) {
                this.dedupFields = [""];
            } else {
                if (this.availableFields.length == 0 && this._getTouchpointDeduplicationFieldsPromise) {
                    // wait for the fields to become available
                    await this._getTouchpointDeduplicationFieldsPromise;
                }
                this.dedupFields = [...this.config.dedupTarget.fields].map(x => this.availableFields.find(y => y.name == x) || x);
            }
        }
    },

    methods: {
        addField() {
            this.dedupFields.push("");
        },

        removeField(index) {
            this.dedupFields.splice(index, 1);
        },

        save() {
            this.$emit("save", {
                dedupTarget: {
                    fields: this.dedupFields.filter(x => x !== "").map(x => x.name),
                    term: this.term
                }
            });
        },

        deleteCheckpoint() {
            this.$emit('delete-connection-checkpoint', { connection: this.connection, checkpoint: 'dedupTarget' });
        },
        
        displayErrors(){
            this.$v.$touch();
        }
    }
}
</script>

<style scoped>
    /* Center label vertically */
    .field-label {
        display: flex;
        align-items: center;
    }
</style>